import React, { useState } from 'react';
import { Lock, Mail, Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface LoginProps {
  onLogin: () => void;
}

export const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
        const { error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });
        if (error) throw error;
        
        // Vérifier s'il y a un paramètre return_to dans l'URL
        const params = new URLSearchParams(window.location.search);
        const returnTo = params.get('return_to');
        
        if (returnTo) {
          // Rediriger vers l'URL spécifiée (pour le SSO Featurebase)
          window.location.href = returnTo;
        } else {
          // Comportement normal : appeler onLogin
          onLogin();
        }
    } catch (err: any) {
        setError(err.message || 'Identifiants incorrects');
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row">
        
        {/* Left Side - Brand */}
        <div className="md:w-1/2 bg-amber-500 p-12 flex flex-col justify-between text-slate-900 relative overflow-hidden">
            <div className="relative z-10 flex flex-col h-full justify-center">
                <div className="bg-white/20 backdrop-blur-sm p-6 rounded-xl inline-block mb-8 self-start">
                     <img 
                        src="https://kfrfvqzjxnadselwusci.supabase.co/storage/v1/object/public/app-assets/logo.png" 
                        alt="PLACID Logo" 
                        className="h-12 w-auto object-contain"
                     />
                </div>
                <h1 className="text-4xl font-bold mb-2">Manager & CRM</h1>
                <p className="text-xl font-medium opacity-90">Espace Professionnel</p>
                <div className="mt-12 border-t border-slate-900/20 pt-8">
                    <p className="font-serif italic text-lg leading-relaxed">
                        La solution connectée pour la gestion de votre activité Placid.
                    </p>
                </div>
            </div>
            <div className="absolute -bottom-24 -right-24 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute -top-24 -left-24 w-64 h-64 bg-slate-900/10 rounded-full blur-3xl"></div>
        </div>

        {/* Right Side - Form */}
        <div className="md:w-1/2 p-12 flex flex-col justify-center">
            <div className="mb-8 text-center md:text-left">
                <h2 className="text-2xl font-bold text-slate-900">
                    Connexion
                </h2>
                <p className="text-slate-500 mt-2">
                    Accédez à votre espace sécurisé
                </p>
            </div>

            <form onSubmit={handleAuth} className="space-y-6">
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                    <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                        <input 
                            type="email" 
                            required
                            className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all bg-white text-slate-900"
                            placeholder="paris@agences-placid.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Mot de passe</label>
                    <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                        <input 
                            type="password" 
                            required
                            className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all bg-white text-slate-900"
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </div>
                </div>

                {error && (
                    <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-100">
                        {error}
                    </div>
                )}

                <button 
                    type="submit"
                    disabled={loading}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 rounded-lg transition-colors shadow-lg shadow-slate-900/20 flex items-center justify-center"
                >
                    {loading ? <Loader className="animate-spin w-5 h-5" /> : "Se connecter"}
                </button>
            </form>
            
            <div className="mt-8 text-center text-xs text-slate-400">
                Accès réservé aux membres du réseau Placid.
            </div>
        </div>
      </div>
    </div>
  );
};